import { useLazyGetOrderListQuery } from '@api/api';
import { OrderFilterData, OrderStatus, OrderType } from '@api/types';
import { Column } from '@components/common';
import { getOrderColumns } from '@components/common/orders/OrderListBlock.config';
import { useAppDispatch, useAppSelector, useAuth } from '@hooks';
import { getTableBlockData, initBlock } from '@store/table/table.slice';
import dayjs from 'dayjs';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

const DEFAULT_ITEMS_PER_PAGE = 20;

type OrderListContextType = {
  id: string;
  itemsPerPage: number;
  items: any;
  isError: boolean;
  isWorking: boolean;
  offset: number;
  totalCount: number;
  filteredColumns: Column[];
  visibleColumnNames?: string[];
  isFetching: boolean;
  filters?: OrderFilterData;
};

type OrderListContextProps = {
  children: React.ReactNode;
  id: string;
  numberItemsOnPage?: number;
  beremaColumnsDisplayed?: string;
  statusesDefault?: OrderStatus;
  visibleFilters?: string;
  orderTypeDefault?: string;
  dealerColumnsDisplayed?: string;
};

export const OrderListContext = createContext<OrderListContextType | null>(null);

export const OrderListProvider = ({
  children,
  id,
  numberItemsOnPage,
  beremaColumnsDisplayed,
  statusesDefault,
  visibleFilters,
  orderTypeDefault,
  dealerColumnsDisplayed,
}: OrderListContextProps) => {
  const itemsPerPage =
    numberItemsOnPage && numberItemsOnPage > 0 ? numberItemsOnPage : DEFAULT_ITEMS_PER_PAGE;
  const dispatch = useAppDispatch();
  const { isBeremaUser } = useAuth();
  const { filters, offset } = useAppSelector(getTableBlockData(id)) as {
    filters?: OrderFilterData;
    offset: number;
  };

  const [trigger, response] = useLazyGetOrderListQuery();
  const { isFetching, isLoading, isError, currentData } = response;
  const orders = currentData?.data?.ordersListFound;
  const totalCount = currentData?.data?.totalCount || 0;
  const isWorking = isLoading || isFetching;
  const visibleColumnNames = useMemo(() => {
    return isBeremaUser
      ? beremaColumnsDisplayed?.split(',').map((x) => x.trim().toLowerCase())
      : dealerColumnsDisplayed?.split(',').map((x) => x.trim().toLowerCase());
  }, [beremaColumnsDisplayed, dealerColumnsDisplayed, isBeremaUser]);
  const filteredColumns = useMemo(
    () =>
      getOrderColumns()?.filter((column) => visibleColumnNames?.includes(column.key.toLowerCase())),
    [visibleColumnNames]
  );

  const visibleFiltersArray = useMemo(() => visibleFilters?.split(',') || [], [visibleFilters]);
  const initialFilters: OrderFilterData = useMemo(
    () => ({
      Search: '',
      ClientStatus: statusesDefault ? (statusesDefault?.split(',') as OrderType[]) : [],
      CreatedOn: '',
      OrderType: orderTypeDefault ? (orderTypeDefault?.split(',') as OrderType[]) : [],
      CustomerNo: '',
    }),
    [statusesDefault, orderTypeDefault]
  );

  const fetchOrders = useCallback(() => {
    const dateFilter = filters?.CreatedOn
      ? dayjs(filters?.CreatedOn).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      : null;

    const requestFilters = [
      {
        filterName: 'Search',
        value: filters?.Search || initialFilters.Search || '',
      },
      {
        filterName: 'ClientStatus',
        value: filters?.ClientStatus?.join('|') || initialFilters?.ClientStatus?.join('|') || '',
      },
      {
        filterName: 'CreatedOn',
        value: dateFilter,
      },
      {
        filterName: 'OrderType',
        value: filters?.OrderType?.join('|') || initialFilters?.OrderType?.join('|') || '',
      },
      {
        filterName: 'CustomerNo',
        value: filters?.CustomerNo || initialFilters?.CustomerNo || '',
      },
    ];
    trigger({
      offSet: offset,
      limit: itemsPerPage,
      filters: requestFilters,
      sorting: {
        fieldToSort: 'Name',
        sortingDirection: 'Desc',
      },
    });
  }, [
    filters?.Search,
    filters?.ClientStatus,
    filters?.CreatedOn,
    filters?.OrderType,
    filters?.CustomerNo,
    offset,
    itemsPerPage,
    trigger,
    initialFilters,
  ]);

  useEffect(() => {
    dispatch(initBlock({ id, filters: initialFilters, visibleFilters: visibleFiltersArray }));
  }, [id, dispatch, initialFilters, visibleFiltersArray]);

  useEffect(() => {
    fetchOrders();
  }, [offset, fetchOrders, filters]);

  const contextValue = useMemo(
    () => ({
      id,
      itemsPerPage,
      items: orders,
      isError,
      isWorking,
      offset,
      totalCount,
      filteredColumns,
      visibleColumnNames,
      isFetching,
      filters,
    }),
    [
      id,
      itemsPerPage,
      orders,
      isError,
      isWorking,
      offset,
      totalCount,
      filteredColumns,
      visibleColumnNames,
      isFetching,
      filters,
    ]
  );
  return <OrderListContext.Provider value={contextValue}>{children}</OrderListContext.Provider>;
};

export const useOrderListContext = () => {
  const context = useContext(OrderListContext);
  if (!context) {
    throw new Error('useOrderListContext must be used within a OrderListProvider');
  }
  return context;
};
